import { Add, Remove } from '@material-ui/icons';
import { FormikNumberField } from '@superdispatch/forms';
import { ColorDynamic, Column, Columns, Stack } from '@superdispatch/ui';
import { Box, Button, TextBox } from '@superdispatch/ui-lab';
import { useField } from 'formik';
import { useCallback, useEffect, useState, useRef } from 'react';
import { HALF_OF_A_SECOND } from 'shared/constants/NumberConstants';
import { formatCurrency } from 'shared/helpers/IntlHelpers';
import { useDebouncedValue } from 'shared/helpers/ReactHelpers';
import styled from 'styled-components';
import {
  useEstimateSeatsUpgrade,
  useEstimateTaxPrice,
  useSubscription,
} from '../SubscriptionAPI';

const StyledNumberField = styled(FormikNumberField)`
  .MuiInputBase-root {
    background-color: ${ColorDynamic.White};
    min-width: 144px;
  }

  @media (max-width: 768px) {
    .MuiInputBase-root {
      min-width: 108px;
    }
  }

  .MuiInputBase-input {
    text-align: center;
  }
`;

const StyledButton = styled(Button)`
  @media (max-width: 768px) {
    padding: 4px 0;
    font-size: 16px;
    min-width: 26px;
  }
`;

const SegmentedNumberField = styled(StyledNumberField)`
  width: 168px;

  && .MuiInputBase-root {
    width: 168px;
    min-width: 0;
    padding: 0;
  }

  .MuiInputBase-input {
    padding-top: 10px;
    padding-bottom: 10px;
    font-weight: 600;
  }
`;

const SegmentedButton = styled(Button)`
  && {
    align-self: stretch;
    flex-shrink: 0;
    width: 48px;
    min-width: 48px;
    padding: 0;
    border: none;
    border-radius: 0;
    box-shadow: none;
    background-color: transparent;
    cursor: pointer;
  }

  &&:disabled {
    cursor: not-allowed;
    pointer-events: auto;
  }

  &&:first-of-type {
    border-right: 1px solid ${ColorDynamic.Silver400};
  }

  &&:last-of-type {
    border-left: 1px solid ${ColorDynamic.Silver400};
  }
`;

interface SeatCounterProps {
  value: number;
  isMinusDisabled: boolean;
  isReadOnly?: boolean;
  isSegmented?: boolean;
  onUpdate: (value: number) => void;
}

function SeatCounter({
  value,
  isMinusDisabled,
  isReadOnly,
  isSegmented,
  onUpdate,
}: SeatCounterProps) {
  const [localValue, setLocalValue] = useState(value);
  const initialValue = useRef(value);
  const debouncedValue = useDebouncedValue(localValue, HALF_OF_A_SECOND);

  useEffect(() => {
    if (debouncedValue === localValue && debouncedValue !== value) {
      onUpdate(debouncedValue);
    }
  }, [debouncedValue, localValue, onUpdate, value]);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const handleDecrease = useCallback(() => {
    if (!isMinusDisabled && localValue > initialValue.current) {
      setLocalValue((prev) => prev - 1);
    }
  }, [isMinusDisabled, localValue]);

  const handleIncrease = useCallback(() => {
    setLocalValue((prev) => prev + 1);
  }, []);

  const isMinusButtonDisabled =
    isMinusDisabled || localValue <= initialValue.current;
  const NumberField = isSegmented ? SegmentedNumberField : StyledNumberField;
  const CounterButton = isSegmented ? SegmentedButton : StyledButton;

  return (
    <NumberField
      name="seats_count"
      value={localValue}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
        setLocalValue(Number(e.target.value));
      }}
      InputProps={{
        readOnly: isReadOnly,
        startAdornment: (
          <CounterButton
            onClick={handleDecrease}
            variant="neutral"
            size="small"
            disabled={isMinusButtonDisabled}
          >
            <Remove
              fontSize="inherit"
              color={isMinusButtonDisabled ? 'inherit' : 'action'}
            />
          </CounterButton>
        ),
        endAdornment: (
          <CounterButton
            onClick={handleIncrease}
            variant="neutral"
            size="small"
          >
            <Add fontSize="inherit" htmlColor={ColorDynamic.Dark100} />
          </CounterButton>
        ),
      }}
      inputProps={{
        decimalScale: 0,
        allowNegative: false,
        allowLeadingZeros: false,
      }}
    />
  );
}

interface SeatBasedPricingBoxProps {
  isMinusDisabled: boolean;
  isReadOnly?: boolean;
  isPlain?: boolean;
}

export function SeatBasedPricingBox({
  isMinusDisabled,
  isReadOnly,
  isPlain,
}: SeatBasedPricingBoxProps) {
  const [field, _, { setValue }] = useField<number>('seats_count');
  const {
    isSubscriptionInTrial,
    isSubscriptionOnLitePlan,
    billableSeats,
    seatType,
  } = useSubscription();

  const isEstimateInvoiceEnabled =
    (isSubscriptionInTrial || isSubscriptionOnLitePlan) &&
    !!billableSeats?.billable_seats;

  const debouncedSeatsCount = useDebouncedValue(field.value, HALF_OF_A_SECOND);

  const { data: taxPrice } = useEstimateTaxPrice(
    { seats_count: debouncedSeatsCount || 1, include_total_invoice: false },
    { enabled: isEstimateInvoiceEnabled, keepPreviousData: true, gcTime: 1000 },
  );

  const { data: estimateSeatsUpgrade } = useEstimateSeatsUpgrade(
    debouncedSeatsCount || 1,
    {
      enabled: !isSubscriptionOnLitePlan && !isSubscriptionInTrial,
      keepPreviousData: true,
      gcTime: 1000,
    },
  );

  const unitPrice =
    taxPrice?.plan?.unit_price || estimateSeatsUpgrade?.plan?.unit_price;

  const isMinusButtonDisabled = field.value <= 1 || isMinusDisabled;

  if (isPlain) {
    return (
      <Columns space="large" align="center">
        <Column width="fluid">
          <Stack space="xxsmall">
            <TextBox variant="body-semibold">Number of Seats</TextBox>
            <TextBox variant="caption" color="secondary">
              {formatCurrency(unitPrice)} per {seatType} per month
            </TextBox>
          </Stack>
        </Column>
        <Column width="content">
          <SeatCounter
            value={field.value}
            isMinusDisabled={isMinusButtonDisabled}
            isReadOnly={isReadOnly}
            isSegmented={true}
            onUpdate={(value) => {
              void setValue(value);
            }}
          />
        </Column>
      </Columns>
    );
  }

  return (
    <Box
      paddingTop="small"
      paddingBottom="small"
      paddingLeft="medium"
      paddingRight="medium"
      backgroundColor="Silver200"
      borderRadius="small"
    >
      <Stack space="small">
        <Columns space="large" align="center">
          <Column width="fluid">
            <Stack space="xxsmall">
              <TextBox variant="heading-6">NUMBER OF NEW SEATS</TextBox>
              <TextBox variant="caption" color="secondary">
                {formatCurrency(unitPrice)} / {seatType} / month
              </TextBox>
            </Stack>
          </Column>
          <Column width="content">
            <Box maxWidth="180px">
              <SeatCounter
                value={field.value}
                isMinusDisabled={isMinusButtonDisabled}
                isReadOnly={isReadOnly}
                onUpdate={(value) => {
                  void setValue(value);
                }}
              />
            </Box>
          </Column>
        </Columns>
      </Stack>
    </Box>
  );
}
